import React from 'react';
import { ArrowUpRight } from 'lucide-react';

const StatCard = ({ 
  icon: Icon, 
  label, 
  value, 
  color,
  trend,           // Optional: "up" | "down" | "stable"
  trendPercent,    // Optional: percentage number
  description,     // Optional: subtitle text
  subtext,         // Optional: additional info at bottom
  onClick,         // Optional: click handler
  gradient,        // Optional: use gradient instead of solid
  size = 'medium'  // Optional: 'small' | 'medium' | 'large'
}) => {
  // Size configurations
  const sizeConfig = {
    small: {
      container: 'p-5',
      icon: 'w-10 h-10',
      iconSize: 20,
      valueSize: 'text-xl',
      labelSize: 'text-xs',
    },
    medium: {
      container: 'p-6 md:p-7',
      icon: 'w-14 h-14',
      iconSize: 28,
      valueSize: 'text-2xl md:text-3xl',
      labelSize: 'text-sm',
    },
    large: {
      container: 'p-8 md:p-10',
      icon: 'w-16 h-16',
      iconSize: 32,
      valueSize: 'text-3xl md:text-4xl',
      labelSize: 'text-base',
    },
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  // Trend color
  const getTrendColor = () => {
    if (trend === 'up') return '#10b981';
    if (trend === 'down') return '#ef4444';
    return '#6b7280';
  };

  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden bg-white rounded-2xl ${config.container} shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 group ${onClick ? 'cursor-pointer' : ''}`}
      style={{
        background: gradient ? `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)` : '#ffffff',
      }}
    >
      {/* Animated background orb */}
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300" style={{ backgroundColor: color }} />
      
      {/* Premium border accent on hover */}
      <div className="absolute top-0 left-0 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent w-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Content Container */}
      <div className="relative z-10 space-y-3">
        {/* Icon and Label Row */}
        <div className="flex items-start justify-between">
          {/* Icon Container */}
          <div
            className={`${config.icon} rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}
            style={{
              background: gradient 
                ? `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`
                : color,
              boxShadow: `0 8px 16px ${color}40`,
            }}
          >
            <Icon size={config.iconSize} strokeWidth={2} />
          </div>

          {/* Trend Badge */}
          {trend && trendPercent && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
              <ArrowUpRight 
                size={14} 
                style={{ 
                  color: getTrendColor(),
                  transform: trend === 'down' ? 'rotate(180deg)' : 'none'
                }}
              />
              <span className="text-xs font-bold" style={{ color: getTrendColor() }}>
                {trendPercent}%
              </span>
            </div>
          )}
        </div>

        {/* Label */}
        <div>
          <p className={`${config.labelSize} font-semibold text-gray-500 uppercase tracking-wider`} style={{ fontFamily: 'Montserrat' }}>
            {label}
          </p>
          {description && (
            <p className="text-xs text-gray-400 mt-1">{description}</p>
          )}
        </div>

        {/* Value */}
        <div className="flex items-baseline gap-2">
          <p className={`${config.valueSize} font-bold text-gray-900`} style={{ fontFamily: 'Montserrat' }}>
            {value}
          </p>
        </div>

        {/* Subtext */}
        {subtext && (
          <p className="text-xs text-gray-400 pt-2 border-t border-gray-200">
            {subtext}
          </p>
        )}
      </div>

      {/* Decorative corner accent */}
      <div className="absolute bottom-0 right-0 w-20 h-20 rounded-tl-full opacity-5 pointer-events-none" style={{ backgroundColor: color }} />
    </div>
  );
};

export default StatCard;